import { FormCountdownSeparator, FormCountdownStyles } from '@/model/FormWheel';

export const EXT_COUNTDOWN_FIXED_DURATION_MAX_SECONDS = 60 * 60;
export const EXT_COUNTDOWN_FIXED_DURATION_DEFAULT_SECONDS = 5 * 60;

export const EXT_COUNTDOWN_DEFAULT_FONT_SIZE = 25;
export const EXT_COUNTDOWN_DEFAULT_SUFFIX_FONT_SIZE = 12;
export const EXT_COUNTDOWN_DEFAULT_DIGIT_BORDER_RADIUS = 4;
export const EXT_COUNTDOWN_DEFAULT_MARGIN_BOTTOM = 10;

export const EXT_COUNTDOWN_PRESET_IDS = ['1', '2', '3', '4'] as const;

export const EXT_COUNTDOWN_PRESET_THUMB_LABELS: Record<string, string> = {
  '1': '整块卡片',
  '2': '分位双格',
  '3': '翻页灰块',
  '4': '极简文字'
};

export const EXT_COUNTDOWN_PRESET_DEMO_UNITS: { key: string; value: string }[] = [
  { key: 'DD', value: '07' },
  { key: 'HH', value: '07' },
  { key: 'MM', value: '07' },
  { key: 'SS', value: '05' }
];

type ExtCountdownPresetBundle = Pick<
  FormCountdownStyles,
  'variant' | 'textColor' | 'fontSize' | 'fontFamily' | 'showTimeUnitLabels' | 'suffixFontSize' | 'suffixTextColor'
> & {
  digitBackground: NonNullable<FormCountdownStyles['digitBackground']>;
  separator: FormCountdownSeparator;
};

/** 预设 2 默认数字字体（配置区/预览区） */
export const EXT_COUNTDOWN_PRESET_HELVETICA_FONT_FAMILY = 'Helvetica, sans-serif';

/** 预设 1、3、4 默认数字字体（配置区/预览区） */
export const EXT_COUNTDOWN_PRESET_ARIAL_FONT_FAMILY = 'Arial, sans-serif';

export function extCountdownPresetSeparatorStyle(variant: string): FormCountdownSeparator {
  switch (variant) {
    case '3':
      return { char: ':', color: '#5c5c5c' };
    case '4':
      return { char: ':', color: '#000000', fontSize: 21, bold: true };
    default:
      return { char: ':', color: '#000000' };
  }
}

export function extCountdownPresetSuffixTextColor(variant: string): string {
  switch (variant) {
    case '2':
      return '#A8A8A8';
    case '3':
      return '#9CA3AF';
    case '4':
      return '#737373';
    default:
      return '#A7A7A7';
  }
}

/** 倒计时数字默认字号（未配 fontSize 时按预设 1–4） */
export function extCountdownPresetDigitFontSize(variant: string): number {
  switch (variant) {
    case '1':
      return 25;
    case '2':
      return 22;
    case '3':
      return 25;
    case '4':
      return 32;
    default:
      return 25;
  }
}

/** 倒计时数字默认字体（未配 fontFamily 时按预设 1–4） */
export function extCountdownPresetDigitFontFamily(variant: string): string {
  if (variant === '2') return EXT_COUNTDOWN_PRESET_HELVETICA_FONT_FAMILY;
  if (variant === '1' || variant === '3' || variant === '4') return EXT_COUNTDOWN_PRESET_ARIAL_FONT_FAMILY;
  return '';
}

/** 底部 DD/HH/MM/SS 标签默认字号（未配 suffixFontSize 时按预设 1–4） */
export function extCountdownPresetSuffixFontSize(variant: string): number {
  switch (variant) {
    case '4':
      return 12;
    default:
      return 14;
  }
}

/** 预设 4：数字与底部 label 纵向间距（配置缩略图与弹窗预览 segment gap 一致） */
export const EXT_COUNTDOWN_PRESET_4_UNIT_LABEL_GAP_PX = 2;

export const EXT_COUNTDOWN_PRESET_BUNDLES: Record<string, ExtCountdownPresetBundle> = {
  '1': {
    variant: '1',
    textColor: '#FFFFFF',
    fontSize: EXT_COUNTDOWN_DEFAULT_FONT_SIZE,
    showTimeUnitLabels: true,
    suffixFontSize: EXT_COUNTDOWN_DEFAULT_SUFFIX_FONT_SIZE,
    suffixTextColor: extCountdownPresetSuffixTextColor('1'),
    digitBackground: { enabled: true, color: '#000000', borderRadius: 4, borderWidth: 0, borderColor: '#000000' },
    separator: { char: ':', color: '#000000' }
  },
  '2': {
    variant: '2',
    textColor: '#FFFFFF',
    fontSize: EXT_COUNTDOWN_DEFAULT_FONT_SIZE,
    showTimeUnitLabels: true,
    suffixFontSize: EXT_COUNTDOWN_DEFAULT_SUFFIX_FONT_SIZE,
    suffixTextColor: extCountdownPresetSuffixTextColor('2'),
    digitBackground: { enabled: true, color: '#000000', borderRadius: 4, borderWidth: 0, borderColor: '#000000' },
    separator: { char: ':', color: '#000000' }
  },
  '3': {
    variant: '3',
    textColor: '#333333',
    fontSize: 25,
    showTimeUnitLabels: true,
    suffixFontSize: EXT_COUNTDOWN_DEFAULT_SUFFIX_FONT_SIZE,
    suffixTextColor: extCountdownPresetSuffixTextColor('3'),
    digitBackground: { enabled: true, color: '#8c8c8c', borderRadius: 6, borderWidth: 0, borderColor: '#8c8c8c' },
    separator: { char: ':', color: '#5c5c5c' }
  },
  '4': {
    variant: '4',
    textColor: '#000000',
    fontSize: 32,
    showTimeUnitLabels: true,
    suffixFontSize: 12,
    suffixTextColor: extCountdownPresetSuffixTextColor('4'),
    digitBackground: { enabled: false, color: 'rgba(0,0,0,0)', borderRadius: 0, borderWidth: 0, borderColor: '#000000' },
    separator: extCountdownPresetSeparatorStyle('4')
  }
};

export function extCountdownBorderRadiusOptToNum(val: string): number {
  const s = String(val).trim();
  if (s === '0') return 0;
  const n = parseInt(s.replace(/px/gi, ''), 10);
  return Number.isNaN(n) ? EXT_COUNTDOWN_DEFAULT_DIGIT_BORDER_RADIUS : n;
}
